/**
 * Wrapper kolem Storage interface z Web Storage API (https://developer.mozilla.org/en-US/docs/Web/API/Storage).
 * Umožňuje typově bezpečné uložení/načtení objektů.
 */
export default class StorageWrapper {

    /**
     * Konstruktor.
     *
     * @param storage implementace storage
     */
    constructor(storage: Storage) {
        this.storage = storage;
    }

    /**
     * Uloží hodnotu napřímo, bez jakékoliv typové kontroly.
     *
     * @param {string} key - klíč
     * @param {*} value - hodnota jakéhokoliv typu, kterou lze reprezentovat jako JSON, tzn. ne undefined nebo function
     */
    set(key: string, value: any) {
        if (typeof key !== 'string') {
            throw new Error('Key is not a string');
        }
        if (value === undefined) {
            // JSON.stringify by vrátilo undefined a to by se uložilo jako string 'undefined'
            throw new Error('Cannot save undefined value into Storage');
        }
        if (typeof value === 'function') {
            // Ukládat funkci do storage je blbost
            throw new Error('Cannot save function into Storage');
        }
        // Konveze do JSON zajistí zachování datoveho typu
        this.storage.setItem(key, JSON.stringify(value));
    }

    /**
     * Uloží hodnotu s typovou konverzí.
     * Pro undefined/null hodnotu namísto toho smaže danou položku.
     * Pouze pro interní použití!
     *
     * @param key - klíč
     * @param [value] - hodnota
     * @param valueConverter - funkce, která zajistí správné přetypování hodnoty
     * @private
     */
    _setTyped<T>(key: string, value: T, valueConverter: any => T) {
        // null nemá cenu ukládat, vzhledem k tomu, že Storage vrací null i pro nenalezenou hodnotu
        if (value != null) {
            this.set(key, valueConverter(value));
        } else {
            this.remove(key);
        }
    }

    /**
     * Uloží hodnotu jako boolean.
     * Pozor hodnota bude násilně zkonvertována na boolean i když může být jiného typu.
     * Pro undefined/null hodnotu namísto toho smaže danou položku.
     *
     * @param {string} key - klíč
     * @param {boolean} [value] - hodnota
     */
    setBoolean(key: string, value: boolean) {
        this._setTyped(key, value, Boolean);
    }

    /**
     * Uloží hodnotu jako číslo.
     * Pozor hodnota bude násilně zkonvertována na číslo i když může být jiného typu.
     * Pro undefined/null hodnotu namísto toho smaže danou položku.
     *
     * @param {string} key - klíč
     * @param {number} [value] - hodnota
     */
    setNumber(key: string, value: number) {
        this._setTyped(key, value, Number);
    }

    /**
     * Uloží hodnotu jako string.
     * Pozor hodnota bude násilně zkonvertována na string i když může být jiného typu.
     * Pro undefined/null hodnotu namísto toho smaže danou položku.
     *
     * @param {string} key - klíč
     * @param {string} [value] - hodnota
     */
    setString(key: string, value: string) {
        this._setTyped(key, value, String);
    }

    /**
     * Uloží hodnotu jako objekt.
     * Pozor hodnota bude násilně zkonvertována na objekt i když může být jiného typu.
     * Pro undefined/null hodnotu namísto toho smaže danou položku.
     *
     * @param {string} key - klíč
     * @param {Object} [value] - hodnota
     */
    setObject(key: string, value: Object) {
        this._setTyped(key, value, Object);
    }

    /**
     * Vrátí uloženou hodnotu napřímo, bez jakékoliv typové kontroly.
     *
     * @param {string} key - klíč
     * @returns {*} hodnota nebo null v případě nenelezení položky či chyby její deserializace
     */
    get(key: string): any {
        const value = this.storage.getItem(key);
        try {
            return JSON.parse(value);
        } catch(error) {
            return null;
        }
    }

    /**
     * Vrátí hodnotu s typovou konverzí.
     * Pouze pro interní použití!
     *
     * @param {string} key - klíč
     * @param {string} expectedType - očekávaný typ hodnoty
     * @param {*} defaultValue - výchozí hodnota vrácená v případě nenalezení nebo v případě,
     *                           že uložená hodnota nebyla požadovaného typu.
     *                           Lze předat i funkci pro lazy vygenerování hodnoty.
     * @returns {*} hodnota
     * @private
     */
    _getTyped(key: string, expectedType: string, defaultValue: any): any {
        const value = this.get(key);
        if (value != null && typeof value === expectedType) {
            return value;
        }
        if (typeof defaultValue === 'function') {
            return defaultValue();
        }
        return defaultValue;
    }

    /**
     * Vrátí hodnotu jako boolean.
     *
     * @param {string} key - klíč
     * @param {boolean} [defaultValue] - výchozí hodnota vrácená v případě nenalezení nebo v případě,
     *                                   že uložená hodnota nebyla boolean. Lze předat i funkci pro
     *                                   lazy vygenerování hodnoty.
     * @returns {boolean} hodnota
     */
    getBoolean(key: string, defaultValue: ?boolean): boolean {
        return this._getTyped(key, 'boolean', defaultValue);
    }

    /**
     * Vrátí hodnotu jako číslo.
     *
     * @param {string} key - klíč
     * @param {number} [defaultValue] - výchozí hodnota vrácená v případě nenalezení nebo v případě,
     *                                  že uložená hodnota nebyla číslo. Lze předat i funkci pro
     *                                  lazy vygenerování hodnoty.
     * @returns {number} hodnota
     */
    getNumber(key: string, defaultValue: ?number): number {
        return this._getTyped(key, 'number', defaultValue);
    }

    /**
     * Vrátí hodnotu jako string.
     *
     * @param {string} key - klíč
     * @param {string} [defaultValue] - výchozí hodnota vrácená v případě nenalezení nebo v případě,
     *                                  že uložená hodnota nebyla string. Lze předat i funkci pro
     *                                  lazy vygenerování hodnoty.
     * @returns {string} hodnota
     */
    getString(key: string, defaultValue: ?string): string {
        return this._getTyped(key, 'string', defaultValue);
    }

    /**
     * Vrátí hodnotu jako objekt.
     *
     * @param {string} key - klíč
     * @param {Object} [defaultValue] - výchozí hodnota vrácená v případě nenalezení nebo v případě,
     *                                  že uložená hodnota nebyla objekt. Lze předat i funkci pro
     *                                  lazy vygenerování hodnoty.
     * @returns {Object} hodnota
     */
    getObject(key: string, defaultValue: ?Object): Object {
        return this._getTyped(key, 'object', defaultValue);
    }

    /**
     * Odstraní hodnotu.
     *
     * @param {string} key - klíč
     */
    remove(key : string) {
        this.storage.removeItem(key);
    }

}
